"use strict"
///include /assets/js/app/p/basket.js

/**
 * @typedef BasketItemSpec
 * @property {string[]} add
 * @property {{[x: string]: number}} quantity
 * @property {string} [payment_type]
 * @property {string} [payment_period]
 * @property {?Object} [configuration]
 * @property {?Object} [option]
 * @property {?string} [group]
 * @property {?string} [internal_identity]
 * @property {boolean} [isUpgrade]
 */

class BasketItem {
    /**
     * Builds the object
     * @param {{ "class-code": string, "configuration": {[x: string]: any} | null, "discount-code": string | null, "forUser": string | null, "group-head": Object | null, "group-name": string | null, "hasValidConfiguration": boolean | null, "identity": string | null, "internal-identity": string | null, "invoice_label": string | null, "is-renewal": boolean | null, "isUpgrade": boolean | null, "item-id": number, "item-price": string|number, "label": string, "line-price": string|number, "line-vat": string, "meta": {tld: string} | null, "name The product name": string, "needs-configuration": boolean, "options": {[x: string]: string} | null, "original-price": number, "ownConfiguration": Object | null, "payment-item-id": string, "paymentOptions": {period: string, type: string}[], "payment-period": string | null, "payment-type": string, "quantity": number, "service-period-months": number, "service-type": string, "userSuppliedConfiguration": Object | null}} item_in
     * @param {ServiceInfoProxy} service_info_proxy
     */
    constructor(item_in, service_info_proxy) {
        this.classCode = item_in["class-code"]
        this.configuration = item_in.configuration
        this.discountCode  = item_in['discount-code'];
        this.hasValidConfiguration = item_in.hasValidConfiguration
        this.id = item_in["item-id"]
        this.identity = item_in.identity
        this.isRenewal = item_in["is-renewal"]
        this._label = item_in.label
        this.meta = {}
        this.needsConfiguration = item_in["needs-configuration"]
        this.originalPrice = +item_in["original-price"]
        this.paymentOptions = item_in.paymentOptions.filter(
            po => po.type == this.paymentType && po.period == this.paymentPeriod
        ).concat(item_in.paymentOptions.filter(
            po => !(
                po.type == this.paymentType && po.period == this.paymentPeriod
            )
        ))
        this.paymentPeriod = item_in["payment-period"]
        this.paymentType = item_in["payment-type"]
        if(item_in["line-price"]) {
            /** @type {number} Price excluding VAT */
            this.price = +item_in["line-price"]
        } else {
            this.price = 0
        }
        this.quantity = item_in.quantity
        this.serviceType = item_in["service-type"]
        this.serviceInfoProxy = service_info_proxy
        this.paymentTypePeriodCache = item_in["paymentTypePeriodCache"] ? item_in["paymentTypePeriodCache"] : this.paymentTypePeriod
        this.preferredRenewal = item_in["preferredRenewal"];
    }

    exportObject() {
        return {
            "class-code": this.classCode,
            "configuration ": this.configuration,
            "discount-code": this.discountCode,
            "hasValidConfiguration": this.hasValidConfiguration,
            "item-id": this.id,
            "identity": this.identity,
            "is-renewal": this.isRenewal,
            "label": this.label,
            "needs-configuration": this.needsConfiguration,
            "original-price": this.originalPrice,
            "payment-period": this.paymentPeriod,
            "payment-type": this.paymentType,
            "line-price": this.price,
            "quantity": this.quantity,
            "service-type": this.serviceType,
            "paymentTypePeriodCache": this.paymentTypePeriodCache,
            "preferredRenewal": this.preferredRenewal,
            "paymentOptions": this.paymentOptions
        };
    }

    /**
     * @type {?boolean} true if this includes a transfer authcode. Null if none
     * is needed.
     */
    get hasAuthCode() {
        if(!this.configuration) return null
        if(!this.configuration.authInfo) {
            this.configuration.authInfo = {pw:null}
        }
        if(
            this.productClass == "domtrans" &&
            !this.identity.match(/[.]\w\w$/)
        ) {
            return !!this.configuration.authInfo.pw
        } else {
            return null
        }
    }
    get isNonContactTransfer() {
        return(
            this.productClass == "domtrans" &&
            (
                this.identity.match(/[.]uk$/i)
            )
        )
    }

    get fullPrice() {
        return +this.originalPrice;
    }

    get isDomain() {
        return this.serviceType == "domain";
    }

    get isOnePound() {
        return this.discountCode == "onepound";
    }

    get isWeb() {
        return !!this.classCode.match(/^web-/);
    }

    get label() {
        if(
            this.serviceType == "vps" &&
            this.configuration &&
            this.configuration.ApplicationId &&
            this.serviceInfoProxy.vpsApps
        ) {
            let app = this.serviceInfoProxy.vpsApps.find(
                a => a.ApplicationId == this.configuration.ApplicationId
            )
            if(app) {
                return `${this._label} with ${app.DisplayName}`
            } else {
                return `${this._label} with pre-installed application`
            }
        } else if (
            this.serviceType == "premium_mailbox" &&
            this.configuration &&
            this.configuration.local &&
            this.configuration.domain
        ) {
            return `${this._label} (${this.configuration.local}@${this.configuration.domain})`;
        } else {
            return this._label
        }
    }
    get originalPriceLabel() {
        return this.originalPrice.toFixed(2)
    }
    get paymentTypeText() {
        switch(this.paymentType) {
            case 'debit':
                return "Direct Debit";
            case 'card':
                return "Card";
            default:
                return this.paymentType;
        }
    }
    get paymentPeriodText() {
        let s = this.paymentPeriod
        return s[0].toUpperCase() + s.substr(1)
    }
    get periodMonths() {
        return this.quantity && (this.quantity * 12)
    }
    get priceLabel() {
        return this.price.toFixed(2)
    }
    get productClass() {
        return this.classCode.replace(/-.*/, "")
    }
    get productSubcode() {
        return this.classCode.replace(/^[^-]*-/, "")
    }

    get productFeatures() {
        return productFeatures[this.classCode] ? productFeatures[this.classCode] : false
    }

    /**
     * @type {string} The payment type+period combo, for some classes which
     * expect that.
     */
    get paymentTypePeriod() {
        return this.paymentType + "/" + this.paymentPeriod
    }
    set paymentTypePeriod(v) {
        [this.paymentType, this.paymentPeriod] = v.split("/", 2)
    }
    get requiresAuthCode() {
        return(
            this.productClass == "domtrans" &&
            this.identity.match(/[.]\w\w[.]\w\w\w$/)
        )
    }
    /**
     * @param {string} name
     */
    matchesIdentity(name) {
        return this.identity.toLowerCase() == name.toLowerCase()
    }
}

class ServiceInfoProxy {
    constructor() {
        this.loading = {
            vpsApps: null
        }
        this.result = {
            vpsApps: null
        }
    }
    /** @type {VPSApplicationIn[]} */
    get vpsApps() {
        if(!this.loading.vpsApps) {
            this.loading.vpsApps = true
            $.ajax({
                method: "get",
                url: `${AnyBasket.inst.appPrefix}/a/platform/vps/Application`,
                xhrFields: {
                    withCredentials: true
                },
            }).done(
                result => this.result.vpsApps = result.result
            )
        }
        return this.result.vpsApps
    }
}

/**
 * Singleton for basket access
 */
class BasketClient {
    /**
     * @type {BasketClient}
     */
    static get inst() {
        if(!this._inst) {
            this._inst = new BasketClient()
        }
        return this._inst
    }
    constructor() {
        this.content = null
        this.loadInProgress = false
        /** @type {?BasketItem[]} */
        this.invalidItems = null
        /** @type {?BasketItem[]} */
        this.items = null
        this.serviceInfoProxy = new ServiceInfoProxy()
        /**
         * @type {?boolean}
         */
        this.validOnly = null
    }

    /**
     * @type {string}
     */
    get appPrefix() {
        if(!this._appPrefix) {
            if("appPrefix" in window) {
                this._appPrefix = window["appPrefix"]
            } else {
                this._appPrefix = (
                    location.href.match(/20i[.]com/) ?
                        "https://my.20i.com" :
                        ""
                )
            }
        }
        return this._appPrefix
    }

    get currency() {
        if( this.content ){
            localStorage.setItem('currency', this.content.currency);
            return this.content.currency;
        } else {
            if( localStorage.getItem('currency') ){
                return localStorage.getItem('currency');
            } else {
                return "GBP"
            }
        }
    }

    get currencySymbol() {
        return this.currencySymbolFor( this.currency );
    }

    get htmlCurrency() {
        switch( this.currency ){
            case 'EUR':
                return "&euro;";
            case 'GBP':
                return "&pound;";
            case 'USD':
                return "&#36;";
            default:
                return "";
        }
    }

    /**
     * @type {BasketItem[]}
     */
    get itemsFlat() {
        return this.items
    }

    get ref() {
        return null
    }

    /**
     * @returns {number}
     */
    get total() {
        if(!this.items) return null
        return this.items.reduce(
            (carry, item) => Math.round((carry + item.price) * 100) / 100,
            0.00
        )
    }
    /**
     * Adds a basket item by spec
     *
     * @param {BasketItemSpec} item
     * @param {boolean} [silent]
     * @returns {JQuery.jqXHR}
     */
    addItem(item, silent = false) {
        return $.ajax({
            method: "post",
            url: `${this.appPrefix}/a/basket/add`,
            data: JSON.stringify(item),
            contentType: "application/json",
            processData: false,
            xhrFields: {
                withCredentials: true
            },
        }).fail(() => {
            if(!silent) {
                ShowNotification.fail(
                    "There was a problem adding an item to the basket."
                )
            }
        })
    }

    /**
     * Adds several items.
     * @param {BasketItemSpec[]} items
     * @param {boolean} [ordered]
     * @returns {JQuery.Promise}
     */
    addItems(items, ordered = false, silent = false) {
        if(ordered) {
            return items.reduce(
                (carry, item) => carry.then(
                    () => this.addItem(item, silent)
                ),
                $.when()
            )
        } else {
            return $.when(
                ...(items.map(item => this.addItem(item, silent)))
            )
        }
    }

    /**
     * Adds several items.
     * @param {BasketItemSpec[]} items
     * @param {boolean} [ordered]
     * @returns {JQuery.Promise}
     */
    addItemsWithTracking(items, ordered = false) {
        if(ordered) {
            return items.reduce(
                (carry, item) => carry.then(
                    () => this.addItemWithTracking(item)
                ),
                $.when()
            )
        } else {
            return $.when(
                ...(items.map(item => this.addItemWithTracking(item)))
            )
        }
    }

    /**
     * Adds a basket item by spec with tracking!
     *
     * @param {BasketItemSpec} item
     * @param {boolean} [silent]
     * @returns {JQuery.jqXHR}
     */
    addItemWithTracking(item, silent = false) {
        return this.addItem(item, silent).done(
            result => {
                if("fbq" in window) {
                    const item = result[0].result
                    /// @ts-ignore
                    window["fbq"]("track", "AddToCart", {
                        content_name: item.label,
                        content_category: item["class-code"],
                        content_ids: [
                            item["product-id"]
                        ],
                        content_type: "product",
                        value: item["line-price"],
                        currency: this.currency,
                    })
                }
            }
        )
    }

    /**
     * Adds a simplified basket item
     *
     * @param {string} code
     * @param {number} qty
     * @param {?string} identity
     * @param {string} payment_type
     * @param {string} payment_period
     * @param {?*} [configuration]
     * @param {?*} [options]
     * @param {?string} [group]
     * @param {boolean} [silent]
     * @returns {JQuery.Promise}
     */
    addSimpleItem(
        code,
        qty,
        identity,
        payment_type,
        payment_period,
        configuration = null,
        options = null,
        group = null,
        silent = false
    ) {
        return this.addItem(
            Object.assign(
                {
                    add: [
                        identity ?
                            `${code}/${identity}` :
                            `${code}/`
                    ],
                    quantity: {
                        [identity ? `${identity}` : ""]: qty
                    },
                    payment_type: payment_type,
                    payment_period: payment_period,
                },
                configuration ? {configuration: configuration} : {},
                options ? {option: options} : {},
                group ? {group: group} : {}
            ),
            silent
        )
    }

    /**
     * Adds a simplified basket item with Tracking
     *
     * @param {string} code
     * @param {number} qty
     * @param {?string} identity
     * @param {string} payment_type
     * @param {string} payment_period
     * @param {?*} [configuration]
     * @param {?*} [options]
     * @param {?string} [group]
     * @param {boolean} [silent]
     * @returns {JQuery.Promise}
     */
    addSimpleItemWithTracking(
        code,
        qty,
        identity,
        payment_type,
        payment_period,
        configuration = null,
        options = null,
        group = null,
        silent = false
    ) {
        return this.addItemWithTracking(
            Object.assign(
                {
                    add: [
                        identity ?
                            `${code}/${identity}` :
                            `${code}/`
                    ],
                    quantity: {
                        [identity ? `${identity}` : ""]: qty
                    },
                    payment_type: payment_type,
                    payment_period: payment_period,
                },
                configuration ? {configuration: configuration} : {},
                options ? {option: options} : {},
                group ? {group: group} : {}
            ),
            silent
        )
    }

    /** Routes the browser to the checkout page */
    goToCheckout() {
        location.href = `${this.appPrefix}/checkout`
    }

    /**
     * Recovers an existing basket (or, just fetches the existing one)
     *
     * Only valid items will be included
     *
     * @param {string} ref
     * @returns {JQuery.jqXHR}
     */
    recover(ref) {
        this.loadInProgress = true
        return $.ajax({
            method: "get",
            url: ref ?
                `${this.appPrefix}/a/recoverPopulatedBasket/${ref}` :
                `${this.appPrefix}/a/basket`,
            data: {
                validOnly: 1,
            }
        }).done(data => {
            this.content = {...data.result}
            this.invalidItems =
                data.result.invalidItems &&
                data.result.invalidItems.map(
                    item => new BasketItem(
                        item,
                        this.serviceInfoProxy
                    )
                )
            this.items = data.result.items.map(
                item => new BasketItem(
                    item,
                    this.serviceInfoProxy
                )
            )
            this.validOnly = true
        }).fail(() => {
            ShowNotification.fail(
                "There was a problem fetching the basket contents."
            )
            this.content = null
        }).always(
            () => this.loadInProgress = false
        )
    }

    /**
     * Get a currency symbol for the given currency
     */
    currencySymbolFor(currency){
        switch( currency ){
            case 'EUR':
                return "\u20AC";
            case 'USD':
                return "\u0024";
            case 'GBP':
            default:
                return "\u00a3";
        }
    }

    /**
     * @deprecated
     * (re-)fetches the basket
     *
     * @param {boolean} [silent]
     * @returns {PromiseLike}
     */
    reload(silent = false) {
        return this.reloadContent(silent)
    }

    /**
     * (re-)fetches the basket
     *
     * @param {boolean} [silent]
     * @returns {PromiseLike}
     */
    reloadContent(silent = false) {
        this.loadInProgress = true
        return $.ajax({
            method: "get",
            url: `${this.appPrefix}/a/basket`,
            data: {
                validOnly: +this.validOnly,
            },
            xhrFields: {
                withCredentials: true
            },
        }).done(
            data => {
                this.content = data.result
                this.invalidItems =
                    data.result.invalidItems &&
                    data.result.invalidItems.map(
                        i => new BasketItem(i, this.serviceInfoProxy)
                    )
                this.items = data.result.items.map(
                    i => new BasketItem(i, this.serviceInfoProxy)
                )

                // This could be improved I think
                // Pass the content to all event listeners
                $(document).trigger(
                    "Basket:updated",
                    [
                        this.content,
                        this.items,
                    ]
                )
            }
        ).fail(
            jqXHR => {
                if(jqXHR.status != 0 && !silent) {
                    ShowNotification.warn(
                        "There was a problem fetching basket contents, please contact support."
                    )
                }
            }
        ).always(() => {
            this.loadInProgress = false
        })
    }

    /**
     * Removes an item.
     *
     * @param {AnyBasketItemInst} item
     * @param {boolean} [silent]
     * @returns {Promise<boolean>}
     */
    async removeItem(item, silent = false) {
        if(item instanceof BasketItem) {
            await this.removeItemAtIndex(item.id, silent)
            return true
        } else {
            return false
        }
    }

    /**
     * Removes an item. The first one is 0.
     *
     * @param {number} id
     * @param {boolean} [silent]
     * @returns {JQuery.jqXHR}
     */
    removeItemAtIndex(id, silent = false) {
        return $.ajax({
            method: "delete",
            url: `${this.appPrefix}/a/basket/item/${id}`,
            xhrFields: {
                withCredentials: true
            },
        }).done(() => {
            if(!silent) {
                ShowNotification.success(
                    "Successfully removed item from basket."
                )
            }
            this.reloadContent(silent)
        }).fail(() => {
            if(!silent) {
                ShowNotification.fail(
                    "There was a problem removing the item from your basket. Please contact support."
                )
            }
        })
    }
    /**
     * Updates a basket item
     * @param {{"item-id": number, [x: string]: *}} item
     * @param {boolean} silent - If true then we wont display any notifications here.
     * @returns {PromiseLike}
     */
    updateItem(item, silent = false) {
        return $.ajax({
            method: "post",
            url: `${this.appPrefix}/a/basket/item/${item['item-id']}`,
            data: JSON.stringify(item),
            contentType: "application/json",
            xhrFields: {
                withCredentials: true
            },
        }).done(() => {
            if( !silent ){
                ShowNotification.success(
                    "Successfully updated item in basket."
                )
            }
            this.reloadContent()
        }).fail(() => {
            if( !silent ){
                ShowNotification.fail(
                    "There was a problem updating an item in the basket."
                )
            }
        })
    }
}

if(typeof AnyBasket != "undefined") {
    AnyBasket.register(BasketClient)
}

const productFeatures = {
    'web-wordpressperset1': [
        '1 WordPress site',
        'Free daily backups',
        'Global CDN & Acceleration Suite',
        'WordPress staging & more'
    ],
    'web-wordpressproset10': [
        '10 WordPress sites',
        'Free daily backups',
        'Global CDN & Acceleration Suite',
        'WordPress staging & more'
    ],
    'web-wordpressagsetINF': [
        'Unlimited WordPress sites',
        'Free daily backups',
        'Global CDN & Acceleration Suite',
        'WordPress staging & more'
    ],
    'reseller-exec': [
        'Host unlimited websites',
        'Completely white-label',
        'Super-fast & reliable cloud hosting',
        'Global CDN & Acceleration Suite'
    ],
    'web-starterset1': [
        '1 website',
        'Expert UK-based support',
        '80+ one-click installs',
        'Global CDN & Acceleration Suite'
    ],
    'web-homeset2': [
        '2 websites',
        'Expert UK-based support',
        '80+ one-click installs',
        'Global CDN & Acceleration Suite'
    ],
    'web-businessset5': [
        '5 websites',
        'Expert UK-based support',
        '80+ one-click installs',
        'Global CDN & Acceleration Suite'
    ],
    'vps-a': [
        '1 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'vps-b': [
        '2 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'vps-c': [
        '4 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'vps-d': [
        '8 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'vps-e': [
        '16 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'vps-f': [
        '32 GB RAM',
        'Unlimited bandwidth',
        '99.99% uptime SLA',
        '1 Tbps+ Anti-DDoS'
    ],
    'web-managedvps2setINF': [
        '2 GB RAM',
        'Global CDN & Acceleration Suite',
        'Full support from our VPS experts',
        'My20i: Like cPanel, but better'
    ],
    'web-managedvps4setINF': [
        '4 GB RAM',
        'Global CDN & Acceleration Suite',
        'Full support from our VPS experts',
        'My20i: Like cPanel, but better'
    ],
    'web-managedvps8setINF': [
        '8 GB RAM',
        'Global CDN & Acceleration Suite',
        'Full support from our VPS experts',
        'My20i: Like cPanel, but better'
    ],
    'web-managedvps16setINF': [
        '16 GB RAM',
        'Global CDN & Acceleration Suite',
        'Full support from our VPS experts',
        'My20i: Like cPanel, but better'
    ],
    'web-managedvps32setINF': [
        '32 GB RAM',
        'Global CDN & Acceleration Suite',
        'Full support from our VPS experts',
        'My20i: Like cPanel, but better'
    ],
}
